/* global railsMode, ohdDomain, ohdDomains */

/**
 * Put global constants here.
 */

export const ONE_REM = 16;
export const CSS_BASE_UNIT = 24;

export const SITE_HEADER_HEIGHT_MOBILE = 5 * ONE_REM;
export const SITE_HEADER_HEIGHT_DESKTOP = 6.25 * ONE_REM;
export const CONTENT_TABS_HEIGHT = 3 * ONE_REM;

// Breakpoints (must match SCSS variables in app/javascript/stylesheets/_variables.scss)
export const SCREEN_M = 768; // $screen-m
export const SCREEN_L = 990; // $screen-l
export const SCREEN_XL = 1280; // $screen-xl

// Buffer space between top of active segment and Video/Menu
export const SPACE_BEFORE_ACTIVE_SEGMENT = 2 * CSS_BASE_UNIT;

export const PROJECT_CDOH = 'cdoh';
export const PROJECT_DG = 'dg';
export const PROJECT_MOG = 'mog';
export const PROJECT_ZWAR = 'zwar';
export const PROJECT_CAMPSCAPES = 'campscapes';

export const DEFAULT_LOCALES = ['de', 'en'];
export const SYSTEM_LOCALES = ['de', 'en', 'el', 'es', 'ru', 'uk', 'ar', 'tr'];

export const VIEWMODE_GRID = 'grid';
export const VIEWMODE_LIST = 'list';
export const VIEWMODE_WORKFLOW = 'workflow';

export const METADATA_SOURCE_INTERVIEW = 'Interview';
export const METADATA_SOURCE_PERSON = 'Person';
export const METADATA_SOURCE_REGISTRY_REFERENCE_TYPE = 'RegistryReferenceType';
export const METADATA_SOURCE_EVENT_TYPE = 'EventType';

// Prefer Rails-injected values from backend constants to avoid duplicated declarations.
const RUNTIME_RAILS_MODE =
    typeof railsMode === 'string' && railsMode ? railsMode : undefined;

// TODO: Define current domain in backend and pass it already processed instead of doing this check in frontend.
export const OHD_DOMAINS =
    typeof ohdDomains === 'object' && ohdDomains !== null ? ohdDomains : {};

export const OHD_LOCATION =
    (typeof ohdDomain === 'string' && ohdDomain) ||
    (RUNTIME_RAILS_MODE ? OHD_DOMAINS[RUNTIME_RAILS_MODE] : undefined);

export const GITHUB_URL = 'https://github.com/oral-history-digital/ohd';

export const ALPHA2_TO_ALPHA3 = {
    de: 'ger',
    en: 'eng',
    el: 'gre',
    es: 'spa',
    ru: 'rus',
    uk: 'ukr',
    ar: 'ara',
    tr: 'tur',
};

export const ALPHA3_TO_ALPHA2 = {
    ger: 'de',
    ita: 'it',
    eng: 'en',
    dut: 'nl',
    fre: 'fr',
    ara: 'ar',
    rus: 'ru',
    gre: 'el',
    bel: 'be',
    bos: 'bs',
    bul: 'bg',
    cze: 'cs',
    heb: 'he',
    hrv: 'hr',
    hun: 'hu',
    lav: 'lv',
    lit: 'lt',
    mac: 'mk',
    nor: 'no',
    per: 'fa',
    pol: 'pl',
    rum: 'ro',
    slo: 'sk',
    slv: 'sl',
    spa: 'es',
    srp: 'sr',
    tam: 'ta',
    tir: 'ti',
    tur: 'tr',
    ukr: 'uk',
    // using alpha-3 for languages without alpha-2
    rom: 'rom',
    cat: 'ca',
    'ukr-rus': 'uk',
    zza: 'zz',
};

const ANALYTICS_URLS = {
    development: '//localhost:8080/',
    staging: '//metrics.oral-history.digital/',
    production: '//metrics.oral-history.digital/',
};
export const ANALYTICS_URL_BASE = ANALYTICS_URLS[railsMode];

export const SHOW_SYSTEM_WARNING = false;

export const CONTRIBUTION_TYPES_SPEAKING = [
    'interviewee',
    'further_interviewee',
    'interviewer',
    'cinematographer',
    'sound',
    'producer',
    'other_attender',
    'interpreter',
];

export const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Password regex requires at least one uppercase letter, one lowercase letter, one number,
// and one special character from the set #?!@$%^&*+=_,.:;~-.
// It also requires the password to be at least 8 characters long.
// IMPORTANT: Keep this regex in sync with the one used in the backend (config/initializers/constants.rb)
// to ensure consistent validation across the application.
export const PASSWORD_REGEX =
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*+=_,.:;~-]).{8,}$/;

// DOMPurify sanitization configurations
export const SANITIZE_CONFIG = {
    // Strip all HTML - for registration errors and plain text content
    PLAIN_TEXT: {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
    },
    // Basic formatting - for banners and simple rich text
    BASIC: {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'del', 's'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
    },
    // Rich text - for CMS content with headings, lists, etc.
    RICH_TEXT: {
        ALLOWED_TAGS: [
            'p',
            'br',
            'strong',
            'em',
            'a',
            'h1',
            'h2',
            'h3',
            'h4',
            'h5',
            'h6',
            'ul',
            'ol',
            'li',
            'blockquote',
            'code',
            'pre',
            'table',
            'thead',
            'tbody',
            'tr',
            'th',
            'td',
            'hr',
            'u',
            'del',
            's',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
    },
    // Search highlighting - preserves only mark tags for Solr highlights
    SEARCH_RESULT: {
        ALLOWED_TAGS: ['mark', 'em', 'strong'],
        ALLOWED_ATTR: [],
    },
};

/**
 * OAI XML export related constants and functions
 */
export const OAI_INSTANCE_IDENTIFIER = 'oral-history.digital';

export const XML_LINK_TYPES = [
    { metadataPrefix: 'oai_datacite', text: 'Datacite' },
    { metadataPrefix: 'oai_dc', text: 'Dublin Core' },
];

// Frames per second for timecode calculations
export const FRAMES_PER_SECOND = 25;

// RTL languages (ISO 639-3 codes)
export const RTL_LANGUAGES = ['ara', 'heb'];
